import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '@shared/services/api';
import AuthLayout from '@shared/layouts/AuthLayout';
import { Card } from '@shared/components/ui/Card';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import {
  PasswordRequirements,
  getPasswordValidation,
} from '@shared/components/PasswordRequirements';

export default function ResetPassword() {
  const { t } = useTranslation(['auth', 'errors']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(!!token);
  const passwordValidation = getPasswordValidation(password);

  useEffect(() => {
    if (!token) {
      setError(t('errors:invalidToken'));
      setTokenValid(false);
    }
  }, [token, t]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');

    if (password !== confirmPassword) {
      setError(t('errors:passwordsDontMatch'));
      return;
    }

    const passwordValidation = getPasswordValidation(password);
    if (!passwordValidation.isValid) {
      setError(t('errors:passwordTooWeak'));
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword({ token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const errorCode = axiosErr.response?.data?.error;
      if (errorCode === 'auth.invalid_token') setError(t('errors:invalidToken'));
      else setError(t('auth:resetPassword.submitError'));
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <AuthLayout>
        <Card className="p-8 text-center animate-in fade-in duration-500">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-2xl bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 mb-8">
            <svg
              className="h-10 w-10 text-state-danger-light dark:text-state-danger-dark"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
            {t('auth:resetPassword.errorTitle')}
          </h2>
          <p
            role="alert"
            className="text-text-muted-light dark:text-text-muted-dark mb-10 font-medium"
          >
            {t('auth:resetPassword.errorMessage')}
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex min-h-11 items-center justify-center px-3 text-center text-xs font-black uppercase tracking-widest text-brand-600 dark:text-brand-400 hover:underline"
          >
            {t('auth:resetPassword.backToLogin')}
          </Link>
        </Card>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout>
        <Card className="p-8 text-center animate-in fade-in zoom-in duration-500">
          <div role="status" aria-live="polite">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-2xl bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 mb-8">
              <svg
                className="h-10 w-10 text-state-ok-light dark:text-state-ok-dark"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
              {t('auth:resetPassword.successTitle')}
            </h2>
            <p className="text-text-muted-light dark:text-text-muted-dark mb-4 font-medium">
              {t('auth:resetPassword.successMessage')}
            </p>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark">
              {t('auth:resetPassword.redirecting')}
            </p>
          </div>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="p-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {t('auth:resetPassword.title')}
          </h2>
          <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark font-medium">
            {t('auth:resetPassword.subtitle')}
          </p>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit}>
          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-l-4 border-state-danger-light dark:border-state-danger-dark p-4 rounded-xl"
            >
              <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                {error}
              </p>
            </div>
          )}

          <div className="space-y-4">
            <Input
              label={t('auth:resetPassword.passwordLabel')}
              type="password"
              autoComplete="new-password"
              required
              disabled={loading}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordTouched(true);
              }}
              placeholder="••••••••••••"
            />

            {passwordTouched && password.length > 0 && (
              <PasswordRequirements
                password={password}
                translationKeyPrefix="resetPassword.passwordRequirements"
              />
            )}

            <Input
              label={t('auth:resetPassword.confirmPasswordLabel')}
              type="password"
              autoComplete="new-password"
              required
              disabled={loading}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••••••"
            />
          </div>

          <Button
            type="submit"
            className="w-full py-4 mt-2"
            isLoading={loading}
            disabled={!passwordValidation.isValid}
          >
            {loading ? t('auth:resetPassword.submitting') : t('auth:resetPassword.submitButton')}
          </Button>
        </form>
      </Card>
    </AuthLayout>
  );
}
